import {
  Component,
  inject,
  signal,
  computed,
  DestroyRef
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Route } from '../../core/enums/route.enum';
import { MenuItem } from '../side-nav.modal';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { UserInfo } from '../../core/models/auth.model';

@Component({
  selector: 'isc-side-nav',
  imports:[CommonModule],
  templateUrl: './side-nav.html',
  styleUrl: './side-nav.scss'
})
export class SideNav {

  // ✅ DI
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  // ✅ UI State
  readonly isHovered = signal(false);
  readonly currentUrl = signal('');

  // ✅ Menu Config (static + controlled)
  readonly menuTop = signal<MenuItem[]>([
    {
      id: 1,
      icon: 'insights_cube.svg',
      label: 'Insights Cube',
      route: Route.INSIGHTS_CUBE,
      show: true,
      selected: false
    },
    {
      id: 2,
      icon: 'bubble-chat.png',
      label: 'Ask Insights Pro',
      route: Route.ASK_INSIGHT_PRO,
      show: true,
      selected: false
    }
  ]);

  readonly menuBottom = signal<MenuItem[]>([
    {
      id: 1,
      icon: 'logout.svg',
      label: 'Logout',
      route: `/${Route.AUTH}/${Route.SIGNIN}`,
      show: true,
      selected: false
    },
    {
      id: 2,
      icon: '',
      label: 'Profile',
      route: `/${Route.PROFILE}`,
      show: true,
      selected: false
    }
  ]);

  // ✅ Derived ViewModel
  readonly vm = computed(() => ({
    hovered: this.isHovered(),
    url: this.currentUrl(),
    menuTop: this.menuTop(),
    menuBottom: this.menuBottom(),
    user: {
      name: (this.auth.getUserInformation<UserInfo>()?.name ? this.auth.getUserInformation<UserInfo>()?.name : this.auth.getUserInformation<UserInfo>()?.first_name + " " + this.auth.getUserInformation<UserInfo>()?.last_name) || 'User',
      role: this.auth.getUserInformation<UserInfo>()?.designation || ''
    }
  }));

  constructor() {
    this.listenToRouteChanges();
  }

  // ✅ Track active route
  private listenToRouteChanges() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: any) => {
        this.currentUrl.set(event.urlAfterRedirects);
      });
  }

  // ✅ UI handlers
  onHover(state: boolean) {
    this.isHovered.set(state);
  }

  isSelected(route: string): boolean {
    return this.currentUrl().includes(route);
  }

  onMenuClick(item: MenuItem) {
    if (item.route.includes(Route.SIGNIN)) {
      this.auth.signOut();
    }

    this.router.navigateByUrl(item.route);
  }

  navigateToProfile() {
    this.router.navigateByUrl(`/${Route.PROFILE}`);
  }
}